import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Users, Plus, X, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import {
  fetchUsers,
  fetchManagedUsers,
  linkManagedUser,
  unlinkManagedUser,
  type User,
} from '@/lib/traccar-api';

interface EditUserManagedUsersDialogProps {
  user: User;
  trigger?: React.ReactNode;
}

export function EditUserManagedUsersDialog({ user, trigger }: EditUserManagedUsersDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [addAllProgress, setAddAllProgress] = useState<{ done: number; total: number } | null>(null);

  const { data: allUsers = [], isLoading: loadingAll } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
    enabled: open,
  });

  const { data: managedUsers = [], isLoading: loadingManaged } = useQuery({
    queryKey: ['managedUsers', user.id],
    queryFn: () => fetchManagedUsers(user.id),
    enabled: open,
  });

  const managedUserIds = new Set(managedUsers.map(u => u.id));

  const linkMutation = useMutation({
    mutationFn: (managedUserId: number) => linkManagedUser(user.id, managedUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedUsers', user.id] });
      toast.success(t('users.userLinked'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('users.userLinkError'));
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (managedUserId: number) => unlinkManagedUser(user.id, managedUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedUsers', user.id] });
      toast.success(t('users.userUnlinked'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('users.userUnlinkError'));
    },
  });

  // Exclude self and already managed users
  const availableUsers = allUsers.filter(
    u => u.id !== user.id && !managedUserIds.has(u.id) &&
      (u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()))
  );

  const handleAddAll = async () => {
    const users = [...availableUsers];
    setAddAllProgress({ done: 0, total: users.length });
    let succeeded = 0;
    let failed = 0;
    for (const u of users) {
      try {
        await linkManagedUser(user.id, u.id);
        succeeded++;
      } catch {
        failed++;
      }
      setAddAllProgress({ done: succeeded + failed, total: users.length });
    }
    setAddAllProgress(null);
    queryClient.invalidateQueries({ queryKey: ['managedUsers', user.id] });
    if (failed === 0) {
      toast.success(t('users.allUsersLinked', { count: succeeded }));
    } else {
      toast.warning(t('users.allUsersLinkedPartial', { succeeded, failed }));
    }
  };

  const isLoading = loadingAll || loadingManaged;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <Users className="h-3.5 w-3.5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('users.editManagedUsers')}</DialogTitle>
          <DialogDescription>
            {t('users.editManagedUsersDescription', { name: user.name })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Managed users */}
            <div>
              <h4 className="text-sm font-medium mb-2">{t('users.associatedUsers')}</h4>
              {managedUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">{t('users.noUsersAssociated')}</p>
              ) : (
                <ScrollArea className="h-[120px]">
                  <div className="flex flex-wrap gap-2">
                    {managedUsers.map(u => (
                      <Badge key={u.id} variant="secondary" className="gap-1 pr-1">
                        {u.name || u.email}
                        <button
                          onClick={() => unlinkMutation.mutate(u.id)}
                          disabled={unlinkMutation.isPending}
                          className="ml-1 rounded-full p-0.5 hover:bg-destructive/20 hover:text-destructive transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* Available users */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">{t('users.availableUsers')}</h4>
                {availableUsers.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={handleAddAll}
                    disabled={!!addAllProgress || linkMutation.isPending}
                  >
                    {addAllProgress ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {addAllProgress.done}/{addAllProgress.total}
                      </>
                    ) : (
                      <>
                        <PlusCircle className="h-3.5 w-3.5" />
                        {t('users.addAll')}
                      </>
                    )}
                  </Button>
                )}
              </div>
              <Input
                placeholder={t('users.searchUsers')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-2"
              />
              <ScrollArea className="h-[160px] border rounded-md">
                {availableUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3">
                    {search ? t('users.noUsersMatch') : t('users.allUsersAssigned')}
                  </p>
                ) : (
                  <div className="p-2 space-y-1">
                    {availableUsers.map(u => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                      >
                        <div className="min-w-0">
                          <p className="text-sm truncate">{u.name || u.email}</p>
                          {u.name && u.email && (
                            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => linkMutation.mutate(u.id)}
                          disabled={linkMutation.isPending}
                          className="h-7 gap-1 flex-shrink-0 ml-2"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {t('common.add')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
